import type { CloudinaryUploadTarget } from "@zplanem/responses"
import { auth } from "../app/firebase"
import { getStoredLocale } from "../app/i18n"

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message)
		this.name = "ApiError"
	}
}

// Empty string in dev → callers keep using relative `/api/...` paths and
// the Vite dev-server proxy (server.proxy in vite.config) forwards them
// to the local API. On a deployed build (Firebase Hosting), there is no
// proxy, so VITE_API_BASE_URL is set at build time to the absolute API
// origin (e.g. https://api-staging.example.com) and is baked into the
// bundle. CORS on the API must allow the Hosting origin.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ""

export const apiUrl = (path: string) => `${API_BASE_URL}${path}`

export async function apiFetch<T>(
	path: string,
	init: RequestInit = {},
): Promise<T> {
	const user = auth.currentUser
	if (!user) {
		throw new ApiError(401, "Not signed in")
	}
	const token = await user.getIdToken()

	const res = await fetch(apiUrl(path), {
		...init,
		headers: {
			...(init.headers ?? {}),
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
			"Accept-Language": getStoredLocale(),
		},
	})

	const isJson = res.headers.get("content-type")?.includes("application/json")
	const body = isJson ? await res.json().catch(() => null) : null

	if (!res.ok) {
		const message =
			(body &&
			typeof body === "object" &&
			"message" in body &&
			typeof body.message === "string"
				? body.message
				: null) ??
			res.statusText ??
			"Request failed"
		throw new ApiError(res.status, message)
	}

	return body as T
}

/**
 * Upload a File directly to Cloudinary with a server-signed payload. This is a
 * raw cross-origin multipart POST — it must NOT carry our Firebase Bearer or a
 * JSON Content-Type; the browser sets the multipart boundary itself.
 */
export async function uploadToCloudinary(
	target: CloudinaryUploadTarget,
	file: File,
): Promise<void> {
	const form = new FormData()
	for (const [key, value] of Object.entries(target.params)) {
		form.append(key, value)
	}
	form.append("file", file)
	const res = await fetch(target.uploadUrl, { method: "POST", body: form })
	if (!res.ok) {
		throw new ApiError(res.status, "Upload failed")
	}
}

/**
 * Upload a File directly to a GCS signed URL. The bytes go as the raw body
 * (NOT multipart) — must NOT carry our Firebase Bearer; the Content-Type must
 * match what the URL was signed for.
 */
export async function uploadToGcs(
	target: {
		uploadUrl: string
		method: string
		headers?: Record<string, string>
	},
	file: File,
): Promise<void> {
	const res = await fetch(target.uploadUrl, {
		method: target.method,
		headers: { "Content-Type": file.type, ...(target.headers ?? {}) },
		body: file,
	})
	if (!res.ok) {
		throw new ApiError(res.status, "Upload failed")
	}
}
